/**
 * Opens B2B checkout footer policy links in a modal with fetched page content.
 */

import { removeTrapFocus, trapFocus } from '@theme/focus';

/** @param {ParentNode} root */
function stripUnsafeNodes(root) {
  root.querySelectorAll('script, iframe, object, embed').forEach((el) => el.remove());
}

/**
 * @param {Document} doc
 * @returns {string}
 */
function extractPolicyHtml(doc) {
  const selectors = [
    '.shopify-policy__container',
    '.shopify-policy__body',
    '#MainContent',
    'main[role="main"]',
  ];

  for (const selector of selectors) {
    const el = doc.querySelector(selector);
    if (el) {
      const clone = /** @type {HTMLElement} */ (el.cloneNode(true));
      stripUnsafeNodes(clone);
      return clone.innerHTML.trim();
    }
  }
  return '';
}

/**
 * @param {HTMLElement} modal
 * @param {string} url
 * @param {string} title
 * @param {AbortSignal} signal
 */
/**
 * Activates focus trap after native `<dialog>` / layout settle (avoids empty focusable lists on first paint).
 * @param {HTMLElement} modalHost - `dialog-component` root
 */
function schedulePolicyModalFocusTrap(modalHost) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const dialogEl = modalHost.querySelector('dialog');
      if (!(dialogEl instanceof HTMLElement) || !dialogEl.open) return;

      const closeBtn = dialogEl.querySelector('.b2b-checkout-policy-modal__close');
      trapFocus(dialogEl, {
        initialFocus: closeBtn instanceof HTMLElement ? closeBtn : undefined,
      });
    });
  });
}

async function fetchAndShowPolicy(modal, url, title, signal) {
  const titleEl = modal.querySelector('[data-policy-modal-title]');
  const bodyEl = modal.querySelector('[data-policy-modal-body]');
  const loadingText = modal.getAttribute('data-loading-text') ?? 'Loading…';
  const errorTemplate = modal.getAttribute('data-error-text') ?? 'Could not load this page.';
  const openNewTabText = modal.getAttribute('data-open-new-tab-text') ?? 'Open in new tab';

  if (!(titleEl instanceof HTMLElement) || !(bodyEl instanceof HTMLElement)) return;

  titleEl.textContent = title;
  bodyEl.innerHTML = `<p class="b2b-checkout-policy-modal__loading">${loadingText}</p>`;
  bodyEl.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(url, {
      signal,
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    let inner = extractPolicyHtml(parsed);

    if (!inner) {
      inner = `<p>${errorTemplate}</p>`;
    }

    bodyEl.innerHTML = inner;
    stripUnsafeNodes(bodyEl);
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') return;

    const escapedUrl = url.replace(/"/g, '&quot;');
    bodyEl.innerHTML = `
      <p class="b2b-checkout-policy-modal__error">${errorTemplate}</p>
      <p><a href="${escapedUrl}" target="_blank" rel="noopener noreferrer" class="b2b-checkout-policy-modal__fallback-link">${openNewTabText}</a></p>
    `;
  } finally {
    bodyEl.removeAttribute('aria-busy');
  }
}

function init() {
  const footer = document.querySelector('.b2b-checkout-footer');
  const modal = document.getElementById('b2b-checkout-policy-modal');

  if (!footer || !(modal instanceof HTMLElement)) return;

  /** @type {AbortController | null} */
  let loadAbort = null;

  /** @type {HTMLAnchorElement | null} */
  let policyTrigger = null;

  modal.addEventListener('dialog:open', () => {
    schedulePolicyModalFocusTrap(modal);
  });

  footer.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('a[data-b2b-policy-modal]');
    if (!(link instanceof HTMLAnchorElement)) return;

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }

    event.preventDefault();

    policyTrigger = link;

    loadAbort?.abort();
    loadAbort = new AbortController();

    const url = link.href;
    const title = link.textContent?.trim() ?? '';

    if (typeof modal.showDialog === 'function') {
      modal.showDialog();
      schedulePolicyModalFocusTrap(modal);
    }

    fetchAndShowPolicy(modal, url, title, loadAbort.signal);
  });

  modal.addEventListener('dialog:close', () => {
    loadAbort?.abort();
    removeTrapFocus();
    policyTrigger?.focus({ preventScroll: true });
    policyTrigger = null;
    const bodyEl = modal.querySelector('[data-policy-modal-body]');
    if (bodyEl instanceof HTMLElement) {
      bodyEl.innerHTML = '';
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
